/**
 * TimelineContent Component
 * Main timeline view with track lanes, chord track, automation, and overlays
 */

import React, { useState, useCallback } from 'react';
import { ChordTrack } from '../chordtrack';
import { AutomationLanesContainer } from '../AutomationLane';
import { RemoteCursors } from '../collaboration';
import {
  InlineTrackLane,
  InlineTimeRuler,
  LoopRegionOverlay,
  Playhead,
  MobileMixerToggle,
  TimelineHeader,
} from './index';

export const TimelineContent = ({
  // Timeline dimensions
  totalDuration,
  zoom,
  setZoom,
  
  // Playback
  currentTime,
  isPlaying,
  seekTo,
  bpm,
  
  // Tracks
  tracks,
  selectedTrack,
  setSelectedTrack,
  
  // Collaboration
  showCollaboration,
  remoteCursors,
  collabUsers,
  currentUserId,
  
  // Loop
  loopEnabled,
  loopStart,
  loopEnd,
  setIsDraggingLoopStart,
  setIsDraggingLoopEnd,
  loopDragStartRef,
  
  // Chord Track
  showChordTrack,
  chordRegions,
  setChordRegions,
  snapEnabled,
  
  // Automation
  showAutomation,
  automationSnapEnabled,
  addAutomationLane,
  deleteAutomationLane,
  updateAutomationLane,
  toggleAutomationLaneVisibility,
  
  // Clip Operations
  clipEditMode,
  selectedClipsForJoin,
  setSelectedClipsForJoin,
  openClipInPianoRoll,
  handleMoveClip,
  handleResizeClip,
  createEmptyMidiClip,
  handleSplitMidiClip,
  handleSplitAudioClip,
  handleJoinMidiClips,
  
  // Waveform
  handleFadeChange,
  handleLoopPointsChange,
  handleTrimChange,
  handleSelectAudioClip,
  selectedAudioClip,
  audioSelection,
  handleAudioSelectionChange,
  
  // Mobile
  isMobile,
  showMixer,
  setShowMixer,
  
  // Time Signature
  timeSignature,
  
  // Sample drop handler (P2 feature)
  onSampleDrop,
  
  // Track management
  duplicateTrack,
  removeTrack,
  toggleMute,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropTargetTrack, setDropTargetTrack] = useState(null);
  
  // Handle drag over timeline
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);
  
  // Handle drag leave timeline
  const handleDragLeave = useCallback((e) => {
    // Only set false if leaving the container entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
      setDropTargetTrack(null);
    }
  }, []);
  
  // Handle drop on timeline
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    setDropTargetTrack(null);
    
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      
      const dropData = JSON.parse(data);
      
      if (dropData.type === 'sample') {
        // Calculate drop position (time and track)
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const dropTime = (x / rect.width) * totalDuration;
        
        // Find which track was dropped on based on Y position
        const y = e.clientY - rect.top;
        const trackHeight = 80; // Approximate track height
        const trackIndex = Math.floor(y / trackHeight);
        const targetTrack = tracks[Math.min(trackIndex, tracks.length - 1)];
        
        // Call the sample drop handler
        if (onSampleDrop && targetTrack) {
          onSampleDrop({
            sample: dropData.sample,
            trackId: targetTrack.id,
            startTime: Math.max(0, dropTime),
          });
        }
      }
    } catch (err) {
      console.error('Error handling drop:', err);
    }
  }, [totalDuration, tracks, onSampleDrop]);
  
  return (
    <main className="flex-1 bg-transparent flex flex-col overflow-hidden relative">
      {/* Mobile Mixer Toggle */}
      <MobileMixerToggle
        isMobile={isMobile}
        showMixer={showMixer}
        setShowMixer={setShowMixer}
      />
      
      {/* Timeline Header */}
      <TimelineHeader zoom={zoom} setZoom={setZoom}>
        <InlineTimeRuler duration={totalDuration} zoom={zoom} />
      </TimelineHeader>
    
      {/* Tracks Timeline */}
      <div 
        className={`flex-1 overflow-auto relative ${isDragOver ? 'ring-2 ring-emerald-500/50 bg-emerald-500/5' : ''}`}
        data-testid="tracks-timeline"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onMouseMove={(e) => {
          // Send cursor position to other collaborators
          if (showCollaboration && window.__collabSendCursor) {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const time = (x / rect.width) * totalDuration / zoom;
            window.__collabSendCursor({ x, y, time });
          }
        }}
        onMouseLeave={() => {
          // Clear cursor when leaving timeline
          if (showCollaboration && window.__collabSendCursor) {
            window.__collabSendCursor(null);
          }
        }}
      >
        {/* String Sauce Logo Watermark Background */}
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none z-0"
        >
          <img
            src="/images/string-sauce-watermark.png"
            alt=""
            style={{
              width: '550px',
              height: 'auto',
              opacity: 0.3,
              filter: 'drop-shadow(0 0 8px rgba(255,140,0,0.9)) drop-shadow(0 0 20px rgba(255,120,0,0.6)) drop-shadow(0 0 45px rgba(200,100,0,0.4)) drop-shadow(0 0 80px rgba(180,80,0,0.2))',
            }}
          />
        </div>
        {/* Loop Region Overlay */}
        <LoopRegionOverlay
          loopEnabled={loopEnabled}
          loopStart={loopStart}
          loopEnd={loopEnd}
          totalDuration={totalDuration}
          zoom={zoom}
          setIsDraggingLoopStart={setIsDraggingLoopStart}
          setIsDraggingLoopEnd={setIsDraggingLoopEnd}
          loopDragStartRef={loopDragStartRef}
        />
        
        {/* Playhead */}
        <Playhead
          currentTime={currentTime}
          totalDuration={totalDuration}
          zoom={zoom}
        />
        
        {/* Remote Cursors - Show other users' cursor positions */}
        {showCollaboration && Object.keys(remoteCursors).length > 0 && (
          <RemoteCursors
            cursors={remoteCursors}
            users={collabUsers}
            currentUserId={currentUserId}
            timelineWidth={document.querySelector('[data-testid="tracks-timeline"]')?.clientWidth || 1000}
            zoom={zoom}
          />
        )}
        
        {/* Chord Track - Logic Pro Style */}
        {showChordTrack && (
          <ChordTrack
            chords={chordRegions}
            onChordsChange={setChordRegions}
            currentTime={currentTime}
            isPlaying={isPlaying}
            zoom={zoom}
            totalDuration={totalDuration}
            bpm={bpm}
            snapEnabled={snapEnabled}
          />
        )}
        
        {/* Track Lanes */}
        <div 
          className="min-w-full"
          style={{ width: `${100 * zoom}%` }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const time = (x / rect.width) * totalDuration;
            seekTo(time);
          }}
        >
          {tracks.map((track) => (
            <div key={track.id}>
              <InlineTrackLane
                track={track}
                isSelected={selectedTrack === track.id}
                totalDuration={totalDuration}
                zoom={zoom}
                bpm={bpm}
                currentTime={currentTime}
                isPlaying={isPlaying}
                onSelect={() => setSelectedTrack(track.id)}
                onClipSelect={(clip) => {
                  setSelectedTrack(track.id);
                }}
                onClipDoubleClick={(clip) => openClipInPianoRoll(clip, track.id)}
                onClipMove={(clipId, newStartTime, createUndo) => handleMoveClip(clipId, newStartTime, createUndo)}
                onClipResize={(clipId, newDuration, createUndo) => handleResizeClip(clipId, newDuration, createUndo)}
                onCreateClip={(trackId, startTime) => {
                  const clip = createEmptyMidiClip(trackId, startTime);
                  if (clip) {
                    openClipInPianoRoll(clip, trackId);
                  }
                }}
                onSplitClip={(trackId, clipId) => handleSplitMidiClip(trackId, clipId, currentTime)}
                onSplitAudio={(trackId) => handleSplitAudioClip(trackId, currentTime)}
                onJoinSelectClip={(trackId, clipId) => {
                  setSelectedClipsForJoin(prev => {
                    const exists = prev.some(s => s.trackId === trackId && s.clipId === clipId);
                    if (exists) {
                      return prev.filter(s => !(s.trackId === trackId && s.clipId === clipId));
                    }
                    return [...prev, { trackId, clipId }];
                  });
                }}
                clipEditMode={clipEditMode}
                selectedClipsForJoin={selectedClipsForJoin}
                onFadeChange={handleFadeChange}
                onLoopChange={handleLoopPointsChange}
                onTrimChange={handleTrimChange}
                onSelectAudioClip={handleSelectAudioClip}
                selectedAudioClip={selectedAudioClip}
                onSeek={seekTo}
                snapEnabled={snapEnabled}
                audioSelection={audioSelection}
                onAudioSelectionChange={handleAudioSelectionChange}
                onDuplicate={duplicateTrack}
                onRemove={removeTrack}
                onToggleMute={toggleMute}
              />
              {/* Automation Lanes */}
              {showAutomation && (
                <AutomationLanesContainer
                  trackId={track.id}
                  lanes={track.automationLanes || []}
                  trackColor={track.color?.includes('cyan') ? 'rgba(6, 182, 212, 0.8)' 
                    : track.color?.includes('purple') ? 'rgba(168, 85, 247, 0.8)'
                    : track.color?.includes('amber') ? 'rgba(245, 158, 11, 0.8)'
                    : track.color?.includes('emerald') ? 'rgba(16, 185, 129, 0.8)'
                    : track.color?.includes('rose') ? 'rgba(244, 63, 94, 0.8)'
                    : track.color?.includes('indigo') ? 'rgba(99, 102, 241, 0.8)'
                    : 'rgba(139, 92, 246, 0.8)'}
                  trackPlugins={track.pluginChain || []}
                  totalDuration={totalDuration}
                  bpm={bpm}
                  gridSize={60 / bpm / 4} // 1/16 note grid
                  snapEnabled={automationSnapEnabled}
                  currentTime={currentTime}
                  isPlaying={isPlaying}
                  onLaneUpdate={(updatedLane, action) => updateAutomationLane(track.id, updatedLane, action)}
                  onAddLane={(trackId, param) => addAutomationLane(trackId, param)}
                  onDeleteLane={(laneId) => deleteAutomationLane(track.id, laneId)}
                  onToggleLaneVisibility={(laneId) => toggleAutomationLaneVisibility(track.id, laneId)}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default TimelineContent;
